import type {
  GetClassResponse,
  GetLocationResponse,
  GetSeasonResponse,
  GetTeacherResponse,
} from "@/openapi/generated";
import { getActivePublicLocations } from "@/lib/utils/locationsPublic";
import { getTeacherPublicShortName } from "@/lib/utils/teacherPublic";

/** Desktop shows this many cards at once; also the minimum count when padding from latest classes. */
export const HOMEPAGE_FEATURED_CLASS_MIN = 3;

/** @deprecated Use HOMEPAGE_FEATURED_CLASS_MIN */
export const FEATURED_HOMEPAGE_CLASS_LIMIT = HOMEPAGE_FEATURED_CLASS_MIN;

function numericId(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Higher id is treated as more recently added (API has no createdAt). */
function byNewestIdDesc<T>(a: T, b: T, readId: (item: T) => unknown): number {
  return numericId(readId(b)) - numericId(readId(a));
}

export function isFeaturedFlag(
  value: { isFeatured?: boolean | null } | null | undefined,
): boolean {
  return value?.isFeatured === true;
}

export function isHomepagePublicClass(
  classItem: GetClassResponse | null | undefined,
): boolean {
  return (
    classItem != null &&
    classItem.isActive !== false &&
    classItem.hideOnLiveSchedule !== true
  );
}

function sortFeaturedClasses(a: GetClassResponse, b: GetClassResponse): number {
  const recency = byNewestIdDesc(a, b, (item) => item.classId);
  if (recency !== 0) return recency;
  return (a.name || "").localeCompare(b.name || "", undefined, {
    sensitivity: "base",
  });
}

/**
 * Featured season if set (prefer active). Otherwise the newest active season,
 * then the newest season of any status.
 */
export function selectFeaturedSeason(
  seasons: GetSeasonResponse[] | null | undefined,
): GetSeasonResponse | null {
  const all = seasons ?? [];
  if (all.length === 0) return null;

  const featured = all.filter(isFeaturedFlag);
  if (featured.length > 0) {
    return (
      featured.find((season) => season.isActive === true) ?? featured[0] ?? null
    );
  }

  const active = all.filter((season) => season.isActive === true);
  const pool = active.length > 0 ? active : all;
  return (
    [...pool].sort((a, b) =>
      byNewestIdDesc(a, b, (item) => item.seasonId),
    )[0] ?? null
  );
}

function selectFeaturedThenPad<T extends { isFeatured?: boolean | null }>(
  publicItems: T[],
  minCount: number,
  getId: (item: T) => unknown,
  sortFeatured: (a: T, b: T) => number,
): T[] {
  if (publicItems.length === 0) return [];

  const featured = publicItems.filter(isFeaturedFlag).sort(sortFeatured);
  if (featured.length >= minCount) return featured;

  const featuredIds = new Set(featured.map(getId));
  const fillers = publicItems
    .filter((item) => !featuredIds.has(getId(item)))
    .sort((a, b) => byNewestIdDesc(a, b, getId));

  if (featured.length === 0) {
    return fillers.slice(0, minCount);
  }

  return [...featured, ...fillers.slice(0, minCount - featured.length)];
}

/**
 * Featured classes first (no max). If fewer than 3, pad with the newest
 * public classes. If none are featured, use the newest 3 public classes.
 */
export function selectFeaturedClasses(
  classes: GetClassResponse[] | null | undefined,
  minCount = HOMEPAGE_FEATURED_CLASS_MIN,
): GetClassResponse[] {
  return selectFeaturedThenPad(
    (classes ?? []).filter(isHomepagePublicClass),
    minCount,
    (item) => item.classId,
    sortFeaturedClasses,
  );
}

/** Desktop shows this many instructor cards at once; also the pad count. */
export const HOMEPAGE_FEATURED_TEACHER_MIN = 3;

/** @deprecated Use HOMEPAGE_FEATURED_TEACHER_MIN */
export const HOMEPAGE_INSTRUCTOR_MAX = HOMEPAGE_FEATURED_TEACHER_MIN;

export const HOMEPAGE_LOCATION_MAX = 3;

function sortFeaturedTeachers(
  a: GetTeacherResponse,
  b: GetTeacherResponse,
): number {
  const nameDelta = getTeacherPublicShortName(a).localeCompare(
    getTeacherPublicShortName(b),
    "en",
    { sensitivity: "base" },
  );
  if (nameDelta !== 0) return nameDelta;
  return numericId(a.teacherId) - numericId(b.teacherId);
}

/**
 * Featured teachers first (no max). If fewer than 3, pad with the newest
 * active teachers. If none are featured, use the newest 3 active teachers.
 */
export function selectFeaturedTeachers(
  teachers: GetTeacherResponse[] | null | undefined,
  minCount = HOMEPAGE_FEATURED_TEACHER_MIN,
): GetTeacherResponse[] {
  const publicTeachers = (teachers ?? []).filter(
    (teacher) => teacher.isActive !== false,
  );
  return selectFeaturedThenPad(
    publicTeachers,
    minCount,
    (item) => item.teacherId,
    sortFeaturedTeachers,
  );
}

/** @deprecated Use selectFeaturedTeachers */
export function selectHomepageInstructors(
  teachers: GetTeacherResponse[] | null | undefined,
): GetTeacherResponse[] {
  return selectFeaturedTeachers(teachers);
}

export function selectHomepageLocations(
  locations: GetLocationResponse[] | null | undefined,
  maxCount = HOMEPAGE_LOCATION_MAX,
): GetLocationResponse[] {
  return getActivePublicLocations(locations ?? undefined).slice(0, maxCount);
}
